import { SORTING_CREATED_AT } from '../pricingPlans/constants';
import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControl, FormLabel, Heading, HStack, Input, Select, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import MainLayout from '../../shared/components/MainLayout';
import { AccessGate, useAllowedMutation, useCan } from '../../shared/access/AccessControls';
import { companiesApi } from '../../store/services/CompaniesService';
import { pricingPlansApi } from '../../store/services/PricingPlansService';
import type { BillingAccountRequest } from '../../store/responseTypes/billingAccounts';
import { isValidUuid } from '../../shared/utils/utils';
import { Paths } from '../../router/routes';
import { platformRoleError } from '../../shared/utils/platformRoleError';

const CompanyPicker = () => {
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const navigate = useNavigate();
  const { currentData, isFetching, isError } = companiesApi.useGetCompaniesStatsQuery({ query, offset, limit: 50 });
  return <Box mb={5}>
    <Heading size="md">Choose a company</Heading>
    <Input aria-label="Find company in statistics" value={query} onChange={e => { setQuery(e.target.value); setOffset(0); }} />
    {isError && <Text role="alert">Could not load company statistics.</Text>}
    <Table><Thead><Tr><Th>Company</Th><Th /></Tr></Thead><Tbody>
      {currentData?.stats.map(c => <Tr key={c.companyId}><Td>{c.companyName}</Td><Td>
        <Button isDisabled={isFetching} onClick={() => navigate(Paths.billingAccounts + '/' + c.companyId)}>Open billing account</Button>
      </Td></Tr>)}
    </Tbody></Table>
    <HStack><Button isDisabled={isFetching || offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>Previous companies</Button>
      <Button isDisabled={isFetching || offset + 50 >= (currentData?.totalEntities || 0)} onClick={() => setOffset(offset + 50)}>Next companies</Button></HStack>
  </Box>;
};

const Account = ({ companyId }: { companyId: string }) => {
  const query = companiesApi.useGetCompanyBillingAccountQuery({ companyId }, { refetchOnMountOrArgChange: true });
  const account = query.currentData?.billingAccount;
  const [hydrated, setHydrated] = useState<typeof query.currentData>();
  const { register, reset, handleSubmit } = useForm<BillingAccountRequest>();
  const [create, creating] = useAllowedMutation(companiesApi.useCreateBillingAccountMutation, 'createBillingAccount');
  const [update, updating] = useAllowedMutation(companiesApi.useUpdateBillingAccountByIdMutation, 'updateBillingAccountById');
  const [remove, removing] = useAllowedMutation(companiesApi.useDeleteBillingAccountByIdMutation, 'deleteBillingAccountById');
  const [assign, assigning] = useAllowedMutation(pricingPlansApi.useAssignBillingAccountMutation, 'assignBillingAccount');
  const [message, setMessage] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [planId, setPlanId] = useState('');
  const canPlans = useCan('ADMIN_TARIFF_PLAN_LIST');
  const canAssign = useCan('ADMIN_TARIFF_PLAN_ASSIGN');
  const plans = pricingPlansApi.useGetPlansQuery({ limit: 50, offset: 0, sorting: SORTING_CREATED_AT, order: 'ASC' }, { skip: !canPlans || !canAssign });
  const canSave = useCan(account?.id ? 'ADMIN_BILLING_ACCOUNT_UPDATE' : 'ADMIN_BILLING_ACCOUNT_CREATE');
  const ready = !!query.currentData && hydrated === query.currentData && !query.isFetching && !query.isError;
  const busy = creating.isLoading || updating.isLoading || removing.isLoading || assigning.isLoading;
  useEffect(() => {
    if (!query.currentData || query.isFetching || query.isError) return;
    const data = query.currentData.billingAccount;
    reset({ name: data?.name || '', address: data?.address || '', city: data?.city || '', country: data?.country || '',
      phone: data?.phone || '', zipCode: data?.zipCode || '', companyId });
    setHydrated(query.currentData); setConfirmDelete(false); setPlanId('');
  }, [query.currentData, query.isFetching, query.isError, reset, companyId]);
  const save = handleSubmit(async values => {
    if (!ready || busy || !canSave) return;
    setMessage('');
    try {
      const data = { ...values, companyId };
      await (account?.id ? update({ accountId: account.id, data }) : create({ data })).unwrap();
      setMessage('Billing account saved.');
    } catch { setMessage('Could not save the billing account. Check the fields and reload before retrying.'); }
  });
  const deleteAccount = async () => {
    if (!ready || !account?.id || busy || !confirmDelete) return;
    try { await remove({ accountId: account.id }).unwrap(); setMessage('Billing account deleted.'); }
    catch (e) { setMessage(platformRoleError(e)); }
  };
  const assignPlan = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready || !account?.id || !planId || !canAssign || plans.isFetching || plans.isError || busy ||
      !plans.currentData?.tariffPlans.some(p => p.id === planId)) return;
    try { await assign({ billingAccountId: account.id, planId }).unwrap(); await query.refetch(); setMessage('Tariff plan assigned.'); }
    catch { setMessage('Could not assign the tariff plan. Reload the account and plans before retrying.'); }
  };
  return <Box>
    <Heading size="md">Billing account for {companyId}</Heading>
    <Button onClick={() => query.refetch()} isDisabled={query.isFetching}>Reload billing account</Button>
    {query.isFetching && <Text role="status">Loading billing account...</Text>}
    {query.isError && <Text role="alert">Could not load this billing account. Changes are disabled.</Text>}
    <form onSubmit={save}>
      <fieldset disabled={!ready || busy || !canSave}>
        {(['name', 'address', 'city', 'country', 'phone', 'zipCode'] as const).map(field =>
          <FormControl key={field}><FormLabel htmlFor={'billing-' + field}>{field}</FormLabel>
            <Input id={'billing-' + field} {...register(field)} type="text" />
          </FormControl>)}
        <AccessGate action={account?.id ? 'ADMIN_BILLING_ACCOUNT_UPDATE' : 'ADMIN_BILLING_ACCOUNT_CREATE'}>
          <Button type="submit" isDisabled={!ready || busy || !canSave}>{account?.id ? 'Update billing account' : 'Create billing account'}</Button>
        </AccessGate>
      </fieldset>
    </form>
    <AccessGate action="ADMIN_BILLING_ACCOUNT_DELETE">{account?.id && <Box mt={4}>
      <Checkbox isChecked={confirmDelete} onChange={e => setConfirmDelete(e.target.checked)}>Confirm deletion of this billing account</Checkbox>
      <Button colorScheme="red" onClick={deleteAccount} isDisabled={!ready || busy || !confirmDelete}>Delete billing account</Button>
    </Box>}</AccessGate>
    <AccessGate action="ADMIN_TARIFF_PLAN_ASSIGN">{account?.id && <form onSubmit={assignPlan}>
      <FormLabel mt={4}>Assign tariff plan</FormLabel>
      <Select aria-label="Tariff plan to assign" value={planId} onChange={e => setPlanId(e.target.value)}>
        <option value="">Choose a tariff plan</option>
        {plans.currentData?.tariffPlans.filter(p => p.isActive && !p.isDeleted).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </Select>
      {plans.isError && <Text role="alert">Could not load tariff plans.</Text>}
      <Button type="submit" isDisabled={!ready || !planId || busy || !canAssign || plans.isFetching || plans.isError}>Assign tariff plan</Button>
    </form>}</AccessGate>
    {message && <Text role="status">{message}</Text>}
  </Box>;
};

const BillingAccounts = () => {
  const { companyId } = useParams();
  const [id, setId] = useState(companyId || '');
  const navigate = useNavigate();
  return <MainLayout><Box p={4}>
    <Heading size="lg" mb={4}>Billing accounts</Heading>
    <AccessGate action="ADMIN_COMPANY_STATS_READ"><CompanyPicker /></AccessGate>
    <form onSubmit={e => { e.preventDefault(); if (isValidUuid(id)) navigate(Paths.billingAccounts + '/' + id); }}>
      <HStack><Input aria-label="Company ID" value={id} onChange={e => setId(e.target.value)} />
        <Button type="submit" isDisabled={!isValidUuid(id)}>Load billing account</Button></HStack>
    </form>
    {companyId && !isValidUuid(companyId) && <Text role="alert">Invalid company ID.</Text>}
    {companyId && isValidUuid(companyId) && <Account key={companyId} companyId={companyId} />}
  </Box></MainLayout>;
};
export default BillingAccounts;
